import { useEffect, useState, useRef } from "react";
import * as Highcharts from "highcharts";

// Selector
import { getLD1 } from "store/slice/charts";

// Hooks
import useChartSlice from "store/slice/charts/useChartSlice";
import useChartResize from "../hooks/useChartResize";
import useChartRender from "../hooks/useChartRender";
import useRenderWatermarkLabels from "../hooks/useRenderWatermarkLabels";
import useTeamCompetitiveComparisonHandlers from "./useTeamCompetitiveComparisonHandlers";

// Config
import { getOption } from "./TeamCompetitiveComparisonConfig";

// Types
import { IBaseChartProps, IChartAxisTitle, IRefObjectForHighchartsReact } from "components/Charts/chart.types";
import { LD1Chart } from "store/slice/charts/chartsSlice.types";
import useTeamCompetitiveAxes from "../hooks/useTeamCompetitiveAxes";
import useCustomiseLabels from "../hooks/useCustomiseLabels";

const useTeamCompetitiveComparison = ({
    width,
    height,
    getData,
}: IBaseChartProps): {
    chartOptions: Highcharts.Options;
    chartData: LD1Chart;
    chartRef: React.MutableRefObject<IRefObjectForHighchartsReact | null>;
    showNames: boolean;
    setShowNames: React.Dispatch<React.SetStateAction<boolean>>;
} => {
    const chartData = useChartSlice(getData) as LD1Chart;
    const chartAxesTitles = useRef<IChartAxisTitle[]>([]);
    const chartRef = useRef<IRefObjectForHighchartsReact>(null);
    const { renderWatermarkLabels } = useRenderWatermarkLabels(chartData);
    const { renderAxesTitles, setTitlesVisible } = useTeamCompetitiveAxes(
        chartAxesTitles,
        chartData,
        chartRef,
        "teamsCompetitiveComparison"
    );
    const selectedSeriesIdxs = useRef<number[]>(
        chartData.series.reduce((acc: number[], serie, index: number) => (serie.visible ? [...acc, index] : acc), [])
    );

    const load = useTeamCompetitiveComparisonHandlers(chartAxesTitles, renderWatermarkLabels);

    const [showNames, setShowNames] = useState(true);

    const [chartOptions, setChartOptions] = useState(() => getOption({ load }, chartData, setTitlesVisible));

    const { chartExtraOptions, resizeReRender, onChartResize } = useChartResize(width, height);

    useCustomiseLabels(false, showNames, setChartOptions, selectedSeriesIdxs.current);
    useChartRender(width, height, chartRef, [onChartResize, renderAxesTitles, renderWatermarkLabels]);

    useEffect(() => {
        const chart = chartRef.current?.chart;
        if (!chart) return;

        setChartOptions((currentValue) => {
            const { chart, credits, legend, series, ...currentValueRest } = currentValue;

            return {
                ...currentValueRest,
                chart: {
                    ...chart,
                    ...chartExtraOptions.current?.chart,
                },
                credits: {
                    ...credits,
                    position: {
                        ...credits?.position,
                        ...chartExtraOptions.current?.credits?.position,
                    },
                },
                legend: {
                    ...legend,
                    ...chartExtraOptions.current?.legend,
                },
                series: chartData?.series,
            };
        });
    }, [chartData, chartExtraOptions, resizeReRender, width, height]);

    return {
        chartOptions,
        chartData,
        chartRef,
        showNames,
        setShowNames,
    };
};

export default useTeamCompetitiveComparison;
